import { useClassesEcole, useNiveauxEcole } from "@/lib/backoffice-hooks";
import type { ClasseLite } from "@/lib/api";

/**
 * Select des classes RÉELLES de l'école, groupées par niveau (ordre
 * pédagogique). Remplace la saisie libre : un libellé tapé à la main
 * (« 6e A » vs « 6eA ») créait un élève sans classe — donc un dû calculé
 * sans les tarifs de classe — en silence. La valeur portée est l'id de
 * classe (non ambigu, accepté par le backend).
 */
export default function ClasseSelect({
  value,
  onChange,
  testId,
  disabled,
}: {
  value: string;
  onChange: (classeId: string) => void;
  testId: string;
  disabled?: boolean;
}) {
  const { data: classes, isLoading: classesLoading } = useClassesEcole();
  const { data: niveaux, isLoading: niveauxLoading } = useNiveauxEcole();

  const tri = (a: ClasseLite, b: ClasseLite) => a.libelle.localeCompare(b.libelle, "fr");
  const niveauxTries = [...(niveaux ?? [])].sort((a, b) => a.ordre - b.ordre);
  const idsNiveaux = new Set(niveauxTries.map((n) => n.id));
  const parNiveau = new Map<string, ClasseLite[]>();
  const orphelines: ClasseLite[] = [];
  for (const c of classes ?? []) {
    if (c.niveauId && idsNiveaux.has(c.niveauId)) {
      const liste = parNiveau.get(c.niveauId) ?? [];
      liste.push(c);
      parNiveau.set(c.niveauId, liste);
    } else {
      orphelines.push(c);
    }
  }
  const chargement = classesLoading || niveauxLoading;

  return (
    <select
      className="w-full border border-slate-300 rounded px-2 h-9 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || chargement}
      data-testid={testId}
    >
      <option value="">{chargement ? "Chargement…" : "Aucune classe"}</option>
      {niveauxTries.map((n) => {
        const cls = (parNiveau.get(n.id) ?? []).sort(tri);
        if (cls.length === 0) return null;
        return (
          <optgroup key={n.id} label={n.libelle}>
            {cls.map((c) => (
              <option key={c.id} value={c.id}>
                {c.libelle}
              </option>
            ))}
          </optgroup>
        );
      })}
      {orphelines.length > 0 && (
        <optgroup label="Autres">
          {orphelines.sort(tri).map((c) => (
            <option key={c.id} value={c.id}>
              {c.libelle}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}
